import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useIzin } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const TL = (v: number) =>
  `₺${Math.abs(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OY_ETIKET: Record<string, string> = {
  nakit: 'Nakit',
  eft: 'EFT',
  kredi_karti: 'Kredi Kartı',
};

const KAYNAK_ETIKET: Record<string, string> = {
  ders_paketi: 'Ders Paketi',
  egitmen: 'Hakedis',
  manuel: 'Manuel',
  hediye: '🎁 Hediye',
};

type Baslik = { id: number; tip: 'gelir' | 'gider'; ad: string; etkinlik_turu: string | null };
type BiletliEtkinlik = { id: string; ad: string; tip: string; tekrarlayan: boolean; gunler: string[] };
type KasaIslem = {
  id: string; tarih: string; tutar: number; tip: 'gelir' | 'gider';
  aciklama: string | null; kategori: string; ilgili_kisi: string | null;
  odeme_yontemi: string | null; kaynak: string; olusturuldu: string;
  etkinlik_adi: string | null; bilet_sayisi: number | null;
  ogrenci_id: string | null;
};
type Kisi = { id: string; ad: string; soyad: string };
type Sekme = 'islemler' | 'kategoriler';

export default function Kasa() {
  usePageTitle('Kasa');
  const navigate = useNavigate();
  const bugun = new Date().toISOString().slice(0, 10);
  const buAy  = bugun.slice(0, 7);
  const MILAT = '2026-05-03';
  const canMuhasebe     = useIzin('muhasebe');
  const canKrediPaket   = useIzin('kredi_paketleri');

  const [sekme, setSekme]           = useState<Sekme>('islemler');
  const [baslangic, setBaslangic]   = useState(MILAT);
  const [bitis, setBitis]           = useState(bugun);
  const [odemeYontemi, setOdemeYontemi] = useState('hepsi');

  const [bakiye, setBakiye]         = useState(0);
  const [toplamGiris, setToplamGiris] = useState(0);
  const [toplamCikis, setToplamCikis] = useState(0);
  const [islemler, setIslemler]     = useState<KasaIslem[]>([]);
  const [basliklar, setBasliklar]   = useState<Baslik[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [aramaMetin, setAramaMetin] = useState('');

  // Manuel kasa modal
  const [modal, setModal]           = useState<'giris' | 'cikis' | null>(null);
  const [form, setForm]             = useState({
    baslik_id: '', tutar: '', aciklama: '', tarih: bugun, odeme_yontemi: 'nakit',
    etkinlik_id: '', bilet_sayisi: '',
  });
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata]             = useState('');
  const [biletliEtkinlikler, setBiletliEtkinlikler] = useState<BiletliEtkinlik[]>([]);

  // Öğrenci bakiye yükleme modali
  const [yukleModal, setYukleModal] = useState(false);
  const [ogrenciler, setOgrenciler] = useState<Kisi[]>([]);
  const [yukleForm, setYukleForm]   = useState({ ogrenci_id: '', tutar: '', aciklama: '', odeme_yontemi: 'nakit' });
  const [yukleArama, setYukleArama] = useState('');
  const [yukleHata, setYukleHata]   = useState('');
  const [yukleKaydediliyor, setYukleKaydediliyor] = useState(false);

  // Eğitmen hakediş ödeme modali
  const [hakedisModal, setHakedisModal] = useState(false);
  const [egitmenler, setEgitmenler] = useState<Kisi[]>([]);
  const [hakedisForm, setHakedisForm] = useState({ egitmen_id: '', odenen_tutar: '', aciklama: '', odeme_yontemi: 'nakit', donem_baslangic: `${buAy}-01`, donem_bitis: bugun });
  const [hakedisArama, setHakedisArama] = useState('');
  const [hakedisHata, setHakedisHata] = useState('');
  const [hakedisKaydediliyor, setHakedisKaydediliyor] = useState(false);

  // Kategori yönetimi
  const [yeniKategori, setYeniKategori] = useState({ tip: 'gelir' as 'gelir' | 'gider', ad: '' });
  const [katKaydediliyor, setKatKaydediliyor] = useState(false);

  const yukle = async () => {
    setYukleniyor(true);
    try {
      const params = new URLSearchParams({ baslangic, bitis });
      if (odemeYontemi !== 'hepsi') params.set('odeme_yontemi', odemeYontemi);
      const [kasaRes, baslikRes] = await Promise.all([
        api.get(`/api/kasa?${params}`),
        api.get('/api/muhasebe/basliklar'),
      ]);
      setBakiye(kasaRes.data.bakiye);
      setToplamGiris(kasaRes.data.toplam_giris);
      setToplamCikis(kasaRes.data.toplam_cikis);
      setIslemler(kasaRes.data.islemler || []);
      setBasliklar(baslikRes.data || []);
    } catch (e) { console.error(e); }
    finally { setYukleniyor(false); }
  };

  useEffect(() => { yukle(); }, [baslangic, bitis, odemeYontemi]);

  const donemEtiket = () => {
    if (baslangic === `${buAy}-01` && bitis === bugun) return 'Bu ay';
    if (baslangic === `${new Date().getFullYear()}-01-01` && bitis === bugun) return 'Bu yıl';
    if (baslangic === MILAT && bitis === bugun) return 'Tümü';
    return `${baslangic} — ${bitis}`;
  };

  const yukleModalAc = async () => {
    setYukleForm({ ogrenci_id: '', tutar: '', aciklama: '', odeme_yontemi: 'nakit' });
    setYukleArama(''); setYukleHata('');
    if (!ogrenciler.length) {
      const r = await api.get('/api/ogrenciler?limit=500').catch(() => ({ data: { ogrenciler: [] } }));
      setOgrenciler(r.data.ogrenciler || r.data || []);
    }
    setYukleModal(true);
  };

  const yukleKaydet = async () => {
    if (!yukleForm.ogrenci_id || !yukleForm.tutar) { setYukleHata('Öğrenci ve tutar zorunlu'); return; }
    setYukleKaydediliyor(true); setYukleHata('');
    try {
      const r = await api.post('/api/kasa/ogrenci-yukle', {
        ogrenci_id: yukleForm.ogrenci_id,
        tutar: parseFloat(yukleForm.tutar),
        aciklama: yukleForm.aciklama || null,
        odeme_yontemi: yukleForm.odeme_yontemi,
      });
      const ogr = ogrenciler.find(o => o.id === yukleForm.ogrenci_id);
      alert(`${ogr ? ogr.ad + ' ' + ogr.soyad : 'Öğrenci'} hesabına ₺${yukleForm.tutar} yüklendi.\nYeni bakiye: ₺${r.data.yeni_bakiye?.toFixed(2)}`);
      setYukleModal(false);
      yukle();
    } catch (e: any) {
      setYukleHata(e.response?.data?.hata || 'Hata oluştu');
    } finally { setYukleKaydediliyor(false); }
  };

  const hakedisModalAc = async () => {
    setHakedisForm({ egitmen_id: '', odenen_tutar: '', aciklama: '', odeme_yontemi: 'nakit', donem_baslangic: `${buAy}-01`, donem_bitis: bugun });
    setHakedisArama(''); setHakedisHata('');
    if (!egitmenler.length) {
      const r = await api.get('/api/egitmenler').catch(() => ({ data: [] }));
      setEgitmenler(r.data || []);
    }
    setHakedisModal(true);
  };

  const hakedisKaydet = async () => {
    if (!hakedisForm.egitmen_id || !hakedisForm.odenen_tutar) { setHakedisHata('Eğitmen ve tutar zorunlu'); return; }
    setHakedisKaydediliyor(true); setHakedisHata('');
    try {
      await api.post('/api/kasa/egitmen-ode', {
        egitmen_id: hakedisForm.egitmen_id,
        odenen_tutar: parseFloat(hakedisForm.odenen_tutar),
        aciklama: hakedisForm.aciklama || null,
        odeme_yontemi: hakedisForm.odeme_yontemi,
        donem_baslangic: hakedisForm.donem_baslangic,
        donem_bitis: hakedisForm.donem_bitis,
      });
      const eg = egitmenler.find(e => e.id === hakedisForm.egitmen_id);
      alert(`${eg ? eg.ad + ' ' + eg.soyad : 'Eğitmen'} için ₺${hakedisForm.odenen_tutar} hakediş ödemesi kaydedildi.`);
      setHakedisModal(false);
      yukle();
    } catch (e: any) {
      setHakedisHata(e.response?.data?.hata || 'Hata oluştu');
    } finally { setHakedisKaydediliyor(false); }
  };

  const modalAc = async (tur: 'giris' | 'cikis') => {
    setForm({ baslik_id: '', tutar: '', aciklama: '', tarih: bugun, odeme_yontemi: 'nakit', etkinlik_id: '', bilet_sayisi: '' });
    setHata('');
    setModal(tur);
    if (tur === 'giris' && !biletliEtkinlikler.length) {
      const r = await api.get('/api/etkinlikler').catch(() => ({ data: [] }));
      const liste = (r.data as BiletliEtkinlik[]).filter(e => ['milonga', 'pratik'].includes(e.tip));
      setBiletliEtkinlikler(liste);
    }
  };

  const islemKaydet = async () => {
    if (!form.tutar) { setHata('Tutar zorunlu'); return; }
    if (!form.baslik_id && !form.etkinlik_id) { setHata('Kategori veya etkinlik seçimi zorunlu'); return; }
    setKaydediliyor(true); setHata('');
    try {
      await api.post('/api/kasa/islem', {
        tip: modal === 'giris' ? 'gelir' : 'gider',
        baslik_id: form.baslik_id,
        tutar: parseFloat(form.tutar),
        aciklama: form.aciklama || null,
        tarih: form.tarih,
        odeme_yontemi: form.odeme_yontemi,
        etkinlik_id: form.etkinlik_id || null,
        bilet_sayisi: form.bilet_sayisi ? parseInt(form.bilet_sayisi) : null,
      });
      setModal(null);
      yukle();
    } catch (e: any) {
      setHata(e.response?.data?.hata || 'Hata oluştu');
    } finally { setKaydediliyor(false); }
  };

  const islemSil = async (id: string) => {
    if (!window.confirm('Bu kasa işlemini silmek istiyor musunuz?')) return;
    try {
      await api.delete(`/api/kasa/islem/${id}`);
      yukle();
    } catch (e: any) {
      alert(e.response?.data?.hata || 'Silinemedi');
    }
  };

  const kategoriEkle = async () => {
    if (!yeniKategori.ad.trim()) return;
    setKatKaydediliyor(true);
    try {
      await api.post('/api/muhasebe/basliklar', { tip: yeniKategori.tip, ad: yeniKategori.ad.trim() });
      setYeniKategori(p => ({ ...p, ad: '' }));
      const r = await api.get('/api/muhasebe/basliklar');
      setBasliklar(r.data || []);
    } catch { alert('Eklenemedi'); }
    finally { setKatKaydediliyor(false); }
  };

  const kategoriSil = async (id: number) => {
    if (!window.confirm('Bu kategoriyi silmek istiyor musunuz?')) return;
    try {
      await api.delete(`/api/muhasebe/basliklar/${id}`);
      setBasliklar(p => p.filter(b => b.id !== id));
    } catch { alert('Silinemedi'); }
  };

  const filtBasliklar = modal ? basliklar.filter(b => b.tip === (modal === 'giris' ? 'gelir' : 'gider')) : [];

  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';

  const btn = (style?: React.CSSProperties) => ({
    border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    padding: '8px 16px', ...style,
  });

  return (
    <div style={{ padding: 24, maxWidth: 1040, margin: '0 auto' }}>
      {/* Başlık */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Şube Kasası</h2>
            <p style={{ margin: 0, marginTop: 4, fontSize: 13, color: textSec }}>Nakit akışı ve kasa bakiyesi</p>
          </div>
          {/* Ana işlem butonları — büyük ve belirgin */}
          <div style={{ display: 'flex', gap: 10 }}>
            {canKrediPaket && <button onClick={yukleModalAc} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px',
              borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
              background: isDark ? 'rgba(79,70,229,0.3)' : 'linear-gradient(135deg, #3730A3, #4F46E5)', color: isDark ? '#a5b4fc' : 'white',
              boxShadow: '0 2px 8px rgba(79,70,229,0.35)',
            }}>
              💳 Öğrenci Bakiye Yükle
            </button>}
            {canMuhasebe && <button onClick={hakedisModalAc} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px',
              borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
              background: 'linear-gradient(135deg, #92400E, #B45309)', color: 'white',
              boxShadow: '0 2px 8px rgba(146,64,14,0.35)',
            }}>
              💰 Hakediş Öde
            </button>}
          </div>
        </div>
        {/* Manuel kasa işlemleri — ikincil, küçük */}
        {canMuhasebe && <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <span style={{ fontSize: 11, color: isDark ? '#555' : '#bbb', alignSelf: 'center', marginRight: 4 }}>Manuel giriş:</span>
          <button onClick={() => modalAc('giris')} style={btn({ background: '#EAF3DE', color: '#085041', padding: '6px 12px', fontSize: 12 })}>
            + Kasa Girişi
          </button>
          <button onClick={() => modalAc('cikis')} style={btn({ background: '#FCEBEB', color: '#A32D2D', padding: '6px 12px', fontSize: 12 })}>
            − Kasa Çıkışı
          </button>
        </div>}
      </div>

      {/* Sekmeler */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `1px solid ${border}` }}>
        {(['islemler', 'kategoriler'] as Sekme[]).map(s => (
          <button key={s} onClick={() => setSekme(s)} style={{
            border: 'none', background: 'none', cursor: 'pointer', padding: '8px 20px',
            fontSize: 13, fontWeight: sekme === s ? 600 : 400,
            color: sekme === s ? '#3C3489' : textSec,
            borderBottom: sekme === s ? '2px solid #3C3489' : '2px solid transparent',
            marginBottom: -1,
          }}>
            {s === 'islemler' ? 'İşlemler' : 'Kategoriler'}
          </button>
        ))}
      </div>

      {sekme === 'islemler' && (
        <>
          {/* Ödeme yöntemi filtresi */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {[['hepsi', 'Hepsi'], ['nakit', 'Nakit'], ['eft', 'EFT'], ['kredi_karti', 'Kredi Kartı']].map(([val, label]) => (
              <button key={val} onClick={() => setOdemeYontemi(val)} style={{
                border: `1px solid ${odemeYontemi === val ? '#3C3489' : '#ddd'}`,
                borderRadius: 20, background: odemeYontemi === val ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card,
                color: odemeYontemi === val ? '#3C3489' : isDark ? '#bbb' : '#555',
                fontWeight: odemeYontemi === val ? 600 : 400,
                padding: '5px 14px', cursor: 'pointer', fontSize: 12,
              }}>
                {label}
              </button>
            ))}
          </div>

          {/* Tarih filtresi */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 16px' }}>
            <span style={{ fontSize: 12, color: textSec, fontWeight: 500 }}>Dönem</span>
            <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
            <span style={{ color: textMuted }}>—</span>
            <input type="date" value={bitis} onChange={e => setBitis(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }} />
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {[['Bu ay', 0], ['Bu yıl', 1], ['Tümü', 2]].map(([label, i]) => (
                <button key={String(label)} onClick={() => {
                  const n = new Date();
                  if (i === 0) { setBaslangic(`${buAy}-01`); setBitis(bugun); }
                  else if (i === 1) { setBaslangic(`${n.getFullYear()}-01-01`); setBitis(bugun); }
                  else { setBaslangic(MILAT); setBitis(bugun); }
                }} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #ddd', background: card, cursor: 'pointer', fontSize: 12, color: isDark ? '#bbb' : '#555' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 3 bakiye kartı */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 24 }}>
            <div style={{ background: card, border: `2px solid ${bakiye >= 0 ? '#1D9E75' : '#e24b4a'}`, borderRadius: 12, padding: '20px 22px' }}>
              <div style={{ fontSize: 11, color: textSec, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Mevcut Bakiye</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: bakiye >= 0 ? '#085041' : '#A32D2D' }}>
                {bakiye < 0 ? '-' : ''}{TL(bakiye)}
              </div>
              <div style={{ marginTop: 8 }}>
                <span style={{ background: bakiye >= 0 ? isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE' : isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: bakiye >= 0 ? '#085041' : '#A32D2D', borderRadius: 20, padding: '2px 10px', fontSize: 11 }}>
                  {odemeYontemi === 'hepsi' ? 'Tüm zamanlarda net' : `${OY_ETIKET[odemeYontemi] || odemeYontemi} — tüm zamanlarda`}
                </span>
              </div>
            </div>
            <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: '20px 22px' }}>
              <div style={{ fontSize: 11, color: textSec, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Toplam Giriş</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#1D9E75' }}>+{TL(toplamGiris)}</div>
              <div style={{ marginTop: 8 }}>
                <span style={{ background: isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE', color: '#085041', borderRadius: 20, padding: '2px 10px', fontSize: 11 }}>{donemEtiket()}</span>
              </div>
            </div>
            <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: '20px 22px' }}>
              <div style={{ fontSize: 11, color: textSec, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Toplam Çıkış</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#e24b4a' }}>−{TL(toplamCikis)}</div>
              <div style={{ marginTop: 8 }}>
                <span style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', borderRadius: 20, padding: '2px 10px', fontSize: 11 }}>{donemEtiket()}</span>
              </div>
            </div>
          </div>

          {/* İşlem tablosu */}
          <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                İşlem Geçmişi
              </span>
              <input
                value={aramaMetin}
                onChange={e => setAramaMetin(e.target.value)}
                placeholder="Açıklama veya kişi ara..."
                style={{ flex: 1, maxWidth: 280, padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}
              />
              {aramaMetin
                ? <span style={{ fontSize: 12, color: textMuted }}>
                    {islemler.filter(ism => [ism.aciklama, ism.ilgili_kisi, ism.kategori].some(f => f?.toLowerCase().includes(aramaMetin.toLowerCase()))).length} sonuç
                  </span>
                : islemler.length > 0 && <span style={{ fontSize: 12, color: textMuted }}>{islemler.length} kayıt</span>
              }
            </div>
            {yukleniyor ? (
              <div style={{ padding: 32, textAlign: 'center', color: textMuted, fontSize: 13 }}>Yükleniyor...</div>
            ) : islemler.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: textMuted, fontSize: 13 }}>Bu dönemde işlem bulunamadı.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${border}`, background: cardHover }}>
                    {['Tarih', 'Kategori / Kaynak', 'Kişi / Açıklama', 'Ödeme', 'Tutar', ''].map(h => (
                      <th key={h} style={{ textAlign: h === 'Tutar' ? 'right' : 'left', padding: '9px 14px', fontSize: 11, color: textMuted, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {islemler.filter(ism =>
                    !aramaMetin || [ism.aciklama, ism.ilgili_kisi, ism.kategori].some(f => f?.toLowerCase().includes(aramaMetin.toLowerCase()))
                  ).map((ism, i) => (
                    <tr key={ism.id} style={{ borderBottom: `1px solid ${borderSub}` }}>
                      <td style={{ padding: '9px 14px', color: isDark ? '#777' : '#777', whiteSpace: 'nowrap' }}>{ism.tarih?.slice(0, 10)}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                            background: ism.tip === 'gelir' ? isDark ? 'rgba(29,158,117,0.15)' : '#EAF3DE' : isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB',
                            color: ism.tip === 'gelir' ? '#085041' : '#A32D2D',
                          }}>{ism.tip === 'gelir' ? 'Giriş' : 'Çıkış'}</span>
                          <span style={{ fontWeight: 500 }}>{ism.kategori}</span>
                          {ism.etkinlik_adi && ism.bilet_sayisi != null && (
                            <span style={{ background: '#F3F0FF', color: '#5B21B6', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>
                              {ism.bilet_sayisi} kişi
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{KAYNAK_ETIKET[ism.kaynak] || ism.kaynak}</div>
                      </td>
                      <td style={{ padding: '9px 14px', color: isDark ? '#999' : '#666', fontSize: 12 }}>
                        {ism.ilgili_kisi && (
                          ism.ogrenci_id
                            ? <div style={{ fontWeight: 500, color: '#3B5BDB', cursor: 'pointer' }} onClick={() => navigate(`/ogrenciler/${ism.ogrenci_id}`)}>{ism.ilgili_kisi}</div>
                            : <div style={{ fontWeight: 500, color: isDark ? '#ccc' : '#444' }}>{ism.ilgili_kisi}</div>
                        )}
                        {ism.aciklama && <div>{ism.aciklama}</div>}
                        {!ism.ilgili_kisi && !ism.aciklama && <span style={{ color: isDark ? '#444' : '#ccc' }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 14px', color: isDark ? '#999' : '#666', fontSize: 12 }}>
                        {ism.odeme_yontemi ? (
                          <span style={{ background: isDark ? '#222222' : '#f0f0f0', borderRadius: 10, padding: '2px 8px', fontSize: 11 }}>
                            {OY_ETIKET[ism.odeme_yontemi] || ism.odeme_yontemi}
                          </span>
                        ) : <span style={{ color: isDark ? '#444' : '#ccc' }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 600, color: ism.tip === 'gelir' ? '#1D9E75' : '#e24b4a', whiteSpace: 'nowrap' }}>
                        {ism.tip === 'gelir' ? '+' : '−'}{TL(parseFloat(String(ism.tutar)))}
                      </td>
                      <td style={{ padding: '9px 8px', textAlign: 'right' }}>
                        {ism.kaynak === 'manuel' && canMuhasebe && (
                          <button onClick={() => islemSil(ism.id)} title="Sil"
                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 15 }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#e24b4a')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#ccc')}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {sekme === 'kategoriler' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {(['gelir', 'gider'] as const).map(tip => (
            <div key={tip} style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${border}`, fontWeight: 600, fontSize: 14, color: tip === 'gelir' ? '#085041' : '#A32D2D' }}>
                {tip === 'gelir' ? '↑ Gelir Kategorileri' : '↓ Gider Kategorileri'}
              </div>
              <div style={{ padding: 12 }}>
                {basliklar.filter(b => b.tip === tip).map(b => (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, marginBottom: 4, background: cardHover }}>
                    <span style={{ fontSize: 13 }}>{b.ad}</span>
                    {canMuhasebe && <button onClick={() => kategoriSil(b.id)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 14 }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#e24b4a')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#ccc')}>✕</button>}
                  </div>
                ))}
                {basliklar.filter(b => b.tip === tip).length === 0 && (
                  <div style={{ fontSize: 12, color: textMuted, padding: '8px 0' }}>Kategori yok</div>
                )}
                {canMuhasebe && <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <input
                    value={yeniKategori.tip === tip ? yeniKategori.ad : ''}
                    onChange={e => setYeniKategori({ tip, ad: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && yeniKategori.tip === tip && kategoriEkle()}
                    placeholder="Yeni kategori adı..."
                    style={{ flex: 1, padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 13 }}
                  />
                  <button
                    onClick={() => { setYeniKategori({ tip, ad: yeniKategori.tip === tip ? yeniKategori.ad : '' }); kategoriEkle(); }}
                    disabled={katKaydediliyor || yeniKategori.tip !== tip || !yeniKategori.ad.trim()}
                    style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: tip === 'gelir' ? '#EAF3DE' : '#FCEBEB', color: tip === 'gelir' ? '#085041' : '#A32D2D', cursor: 'pointer', fontSize: 13, fontWeight: 500, opacity: (!yeniKategori.ad.trim() || yeniKategori.tip !== tip) ? 0.5 : 1 }}>
                    + Ekle
                  </button>
                </div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: 440 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 18, color: modal === 'giris' ? '#085041' : '#A32D2D' }}>
              {modal === 'giris' ? '+ Kasa Girişi' : '− Kasa Çıkışı'}
            </div>

            {/* Ödeme yöntemi */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: textSec, marginBottom: 6, fontWeight: 500 }}>Ödeme Yöntemi</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['nakit', 'Nakit'], ['eft', 'EFT'], ['kredi_karti', 'Kredi Kartı']].map(([val, label]) => (
                  <button key={val} onClick={() => setForm(p => ({ ...p, odeme_yontemi: val }))}
                    style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${form.odeme_yontemi === val ? '#3C3489' : '#ddd'}`, background: form.odeme_yontemi === val ? isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE' : card, color: form.odeme_yontemi === val ? '#3C3489' : isDark ? '#bbb' : '#555', fontWeight: form.odeme_yontemi === val ? 600 : 400, cursor: 'pointer', fontSize: 12 }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Kategori */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: textSec, marginBottom: 6, fontWeight: 500 }}>Kategori</div>
              {filtBasliklar.length === 0 ? (
                <div style={{ fontSize: 12, color: textMuted, padding: '8px 0' }}>
                  Kategori yok — "Kategoriler" sekmesinden ekleyin.
                </div>
              ) : (
                <select
                  value={form.baslik_id}
                  onChange={e => {
                    const yeniBaslikId = e.target.value;
                    const yeniBaslik = basliklar.find(b => String(b.id) === yeniBaslikId);
                    setForm(p => {
                      const mevcutEtkinlik = biletliEtkinlikler.find(ev => ev.id === p.etkinlik_id);
                      const uyumsuz = !!yeniBaslik?.etkinlik_turu && !!mevcutEtkinlik && mevcutEtkinlik.tip !== yeniBaslik.etkinlik_turu;
                      return { ...p, baslik_id: yeniBaslikId, ...(uyumsuz ? { etkinlik_id: '', bilet_sayisi: '' } : {}) };
                    });
                  }}
                  style={{ width: '100%', padding: '9px 10px', borderRadius: 7, border: `1px solid ${form.baslik_id ? (modal === 'giris' ? '#1D9E75' : '#e24b4a') : '#ddd'}`, fontSize: 13, background: card, color: form.baslik_id ? isDark ? '#e0e0e0' : '#333' : textMuted }}
                >
                  <option value=''>— Kategori seçin —</option>
                  {filtBasliklar.map(b => (
                    <option key={b.id} value={String(b.id)}>{b.ad}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Etkinlik + Bilet sayısı — sadece gelir girişinde */}
            {modal === 'giris' && (() => {
              const secilenBaslik = basliklar.find(b => String(b.id) === form.baslik_id);
              const etkinlikSecenekleri = secilenBaslik?.etkinlik_turu
                ? biletliEtkinlikler.filter(e => e.tip === secilenBaslik.etkinlik_turu)
                : biletliEtkinlikler;
              return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 500 }}>
                  Etkinlik <span style={{ color: '#bbb', fontWeight: 400 }}>(opsiyonel)</span>
                  {secilenBaslik?.etkinlik_turu && (
                    <span style={{ color: '#6B46C1', fontWeight: 500 }}> — sadece "{secilenBaslik.ad}" ile eşleşenler gösteriliyor</span>
                  )}
                </div>
                <select
                  value={form.etkinlik_id}
                  onChange={e => setForm(p => ({ ...p, etkinlik_id: e.target.value, bilet_sayisi: '' }))}
                  style={{ width: '100%', padding: '9px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 13, background: card, color: form.etkinlik_id ? isDark ? '#e0e0e0' : '#333' : textMuted, marginBottom: form.etkinlik_id ? 8 : 0 }}
                >
                  <option value=''>— Etkinlik seç (bilet takibi için) —</option>
                  {etkinlikSecenekleri.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.ad}{e.tekrarlayan && e.gunler?.length ? ` · ${e.gunler.join(', ')}` : ''}
                    </option>
                  ))}
                </select>
                {form.etkinlik_id && (
                  <div>
                    <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Kaç kişi katıldı?</div>
                    <input
                      type="number" min="0" value={form.bilet_sayisi}
                      onChange={e => setForm(p => ({ ...p, bilet_sayisi: e.target.value }))}
                      placeholder="Katılımcı sayısı"
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #6B46C1', fontSize: 13, boxSizing: 'border-box' as const }}
                    />
                  </div>
                )}
              </div>
              );
            })()}

            {/* Tutar + Tarih */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Tutar (₺)</div>
                <input type="number" value={form.tutar} onChange={e => setForm(p => ({ ...p, tutar: e.target.value }))}
                  placeholder="0.00" min="0" step="0.01"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Tarih</div>
                <input type="date" value={form.tarih} onChange={e => setForm(p => ({ ...p, tarih: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Açıklama */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 500 }}>Açıklama (opsiyonel)</div>
              <input value={form.aciklama} onChange={e => setForm(p => ({ ...p, aciklama: e.target.value }))}
                placeholder="Örnek: Kira ödemesi, market alışverişi..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {hata && (
              <div style={{ background: isDark ? 'rgba(228,75,74,0.15)' : '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{hata}</div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)}
                style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${border}`, background: card, cursor: 'pointer', fontSize: 13 }}>
                İptal
              </button>
              <button onClick={islemKaydet} disabled={kaydediliyor}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  background: modal === 'giris' ? '#1D9E75' : '#e24b4a', color: 'white', opacity: kaydediliyor ? 0.7 : 1 }}>
                {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Öğrenci Bakiye Yükleme Modali */}
      {yukleModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setYukleModal(false); }}>
          <div style={{ background: card, borderRadius: 12, padding: 24, width: 460 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: '#3730A3' }}>💳 Öğrenci Bakiye Yükle</div>
            <div style={{ fontSize: 12, color: textSec, marginBottom: 18 }}>Öğrencinin genel kredi bakiyesine yükleme yapar.</div>

            {/* Ödeme yöntemi */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: textSec, marginBottom: 6, fontWeight: 500 }}>Ödeme Yöntemi</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['nakit','Nakit'],['eft','EFT'],['kredi_karti','Kredi Kartı']].map(([val, label]) => (
                  <button key={val} onClick={() => setYukleForm(p => ({ ...p, odeme_yontemi: val }))}
                    style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${yukleForm.odeme_yontemi === val ? '#3730A3' : '#ddd'}`, background: yukleForm.odeme_yontemi === val ? isDark ? 'rgba(55,48,163,0.2)' : '#EEF2FF' : card, color: yukleForm.odeme_yontemi === val ? '#3730A3' : isDark ? '#bbb' : '#555', fontWeight: yukleForm.odeme_yontemi === val ? 600 : 400, cursor: 'pointer', fontSize: 12 }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Öğrenci seçimi */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 500 }}>Öğrenci</div>
              <input value={yukleArama} onChange={e => { setYukleArama(e.target.value); setYukleForm(p => ({ ...p, ogrenci_id: '' })); }}
                placeholder="Ad, soyad veya e-posta ara..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box', marginBottom: 6 }} />
              {yukleArama.length >= 2 && !yukleForm.ogrenci_id && (
                <div style={{ border: '1px solid #eee', borderRadius: 8, maxHeight: 180, overflowY: 'auto' }}>
                  {ogrenciler.filter(o => `${o.ad} ${o.soyad}`.toLowerCase().includes(yukleArama.toLowerCase())).slice(0, 8).map(o => (
                    <div key={o.id} onClick={() => { setYukleForm(p => ({ ...p, ogrenci_id: o.id })); setYukleArama(`${o.ad} ${o.soyad}`); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                      {o.ad} {o.soyad}
                    </div>
                  ))}
                </div>
              )}
              {yukleForm.ogrenci_id && (
                <div style={{ background: '#EEF2FF', color: '#3730A3', padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                  ✓ {yukleArama}
                </div>
              )}
            </div>

            {/* Tutar */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 }}>Tutar (₺)</div>
              <input type="number" value={yukleForm.tutar} onChange={e => setYukleForm(p => ({ ...p, tutar: e.target.value }))}
                placeholder="0.00" min="0" step="0.01"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {/* Açıklama */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 }}>Açıklama (opsiyonel)</div>
              <input value={yukleForm.aciklama} onChange={e => setYukleForm(p => ({ ...p, aciklama: e.target.value }))}
                placeholder="Örnek: Ocak ayı kredisi"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {yukleHata && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{yukleHata}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setYukleModal(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 13 }}>İptal</button>
              <button onClick={yukleKaydet} disabled={yukleKaydediliyor}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: '#3730A3', color: 'white', opacity: yukleKaydediliyor ? 0.7 : 1 }}>
                {yukleKaydediliyor ? 'Yükleniyor...' : 'Bakiye Yükle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Eğitmen Hakediş Ödeme Modali */}
      {hakedisModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setHakedisModal(false); }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 460 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: '#92400E' }}>💰 Eğitmen Hakediş Ödemesi</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 18 }}>Hakediş ödemesi kaydeder ve kasa çıkışı olarak işler.</div>

            {/* Ödeme yöntemi */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 500 }}>Ödeme Yöntemi</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['nakit','Nakit'],['eft','EFT'],['kredi_karti','Kredi Kartı']].map(([val, label]) => (
                  <button key={val} onClick={() => setHakedisForm(p => ({ ...p, odeme_yontemi: val }))}
                    style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${hakedisForm.odeme_yontemi === val ? '#92400E' : '#ddd'}`, background: hakedisForm.odeme_yontemi === val ? '#FEF3E2' : 'white', color: hakedisForm.odeme_yontemi === val ? '#92400E' : '#555', fontWeight: hakedisForm.odeme_yontemi === val ? 600 : 400, cursor: 'pointer', fontSize: 12 }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Eğitmen seçimi */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 500 }}>Eğitmen</div>
              <input value={hakedisArama} onChange={e => { setHakedisArama(e.target.value); setHakedisForm(p => ({ ...p, egitmen_id: '' })); }}
                placeholder="Ad veya soyad ara..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box', marginBottom: 6 }} />
              {hakedisArama.length >= 2 && !hakedisForm.egitmen_id && (
                <div style={{ border: '1px solid #eee', borderRadius: 8, maxHeight: 160, overflowY: 'auto' }}>
                  {egitmenler.filter(e => `${e.ad} ${e.soyad}`.toLowerCase().includes(hakedisArama.toLowerCase())).slice(0, 6).map(e => (
                    <div key={e.id} onClick={() => { setHakedisForm(p => ({ ...p, egitmen_id: e.id })); setHakedisArama(`${e.ad} ${e.soyad}`); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}
                      onMouseEnter={ev => (ev.currentTarget.style.background = '#f5f5f5')}
                      onMouseLeave={ev => (ev.currentTarget.style.background = 'white')}>
                      {e.ad} {e.soyad}
                    </div>
                  ))}
                </div>
              )}
              {hakedisForm.egitmen_id && (
                <div style={{ background: '#FEF3E2', color: '#92400E', padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                  ✓ {hakedisArama}
                </div>
              )}
            </div>

            {/* Tutar + Dönem */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 }}>Tutar (₺)</div>
                <input type="number" value={hakedisForm.odenen_tutar} onChange={e => setHakedisForm(p => ({ ...p, odenen_tutar: e.target.value }))}
                  placeholder="0.00" min="0" step="0.01"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 }}>Dönem Başlangıç</div>
                <input type="date" value={hakedisForm.donem_baslangic} onChange={e => setHakedisForm(p => ({ ...p, donem_baslangic: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 }}>Dönem Bitiş</div>
                <input type="date" value={hakedisForm.donem_bitis} onChange={e => setHakedisForm(p => ({ ...p, donem_bitis: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Açıklama */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4, fontWeight: 500 }}>Açıklama (opsiyonel)</div>
              <input value={hakedisForm.aciklama} onChange={e => setHakedisForm(p => ({ ...p, aciklama: e.target.value }))}
                placeholder="Örnek: Ocak ayı hakediş ödemesi"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {hakedisHata && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{hakedisHata}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setHakedisModal(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontSize: 13 }}>İptal</button>
              <button onClick={hakedisKaydet} disabled={hakedisKaydediliyor}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: '#92400E', color: 'white', opacity: hakedisKaydediliyor ? 0.7 : 1 }}>
                {hakedisKaydediliyor ? 'Kaydediliyor...' : 'Ödeme Yap'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
